import { useCallback, useEffect, useState } from 'react';
import { api, type Nota } from '../api';
import { AvisoError, Cabecera, Cargando, Vacio } from '../componentes';
import { conCache } from '../offline/cache';
import { TarjetaNota } from './TarjetaNota';

/**
 * Las notas ya cerradas: contabilizadas, pagadas o canceladas.
 *
 * Vive en su propia pantalla y no al final de la principal porque son cosas
 * distintas. La principal es la mesa de trabajo —lo que aún puedes tocar— y
 * mezclarla con el archivo la llena de ruido justo cuando lo que quieres es
 * meter un ticket rápido.
 *
 * Se piden solo al entrar aquí: son notas terminadas, no cambian, y traerlas en
 * cada arranque sería pagar una petición por algo que casi nunca se mira.
 */
export function Historico({
  alAbrirNota,
  alVolver,
}: {
  alAbrirNota: (id: number, nombre: string) => void;
  alVolver: () => void;
}) {
  const [notas, setNotas] = useState<Nota[] | null>(null);
  const [desdeCache, setDesdeCache] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const resultado = await conCache('notas:historico', () => api.notas(true));
      setNotas(resultado.datos);
      setDesdeCache(resultado.desdeCache);
    } catch (fallo) {
      setError(fallo);
      setNotas([]);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  return (
    <>
      <Cabecera titulo="Histórico" alVolver={alVolver} />

      {desdeCache && (
        <div className="barra-estado">Sin conexión: esto es lo último que vimos.</div>
      )}

      <div className="contenido">
        {!desdeCache && <AvisoError error={error} />}

        {notas === null ? (
          <Cargando />
        ) : notas.length === 0 ? (
          <Vacio
            titulo="Aquí no hay nada todavía."
            sugerencia="Tus notas aparecerán cuando se hayan contabilizado o pagado."
          />
        ) : (
          <>
            <p className="tarjeta-meta">
              Notas ya cerradas. Se muestran las {notas.length === 1 ? 'última' : 'últimas'}{' '}
              {notas.length}.
            </p>
            <ul className="lista">
              {notas.map((nota) => (
                <TarjetaNota
                  key={nota.id}
                  nota={{ ...nota, ref: nota.id, pendiente: false }}
                  alAbrir={() => alAbrirNota(nota.id, nota.name)}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </>
  );
}
